import { Clock, MousePointer2, Globe, Lock, Swords } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Run, Match, MODE_LABELS, DIFFICULTY_LABELS } from '@/types/game';
import { formatTime } from '@/lib/utils';

interface RunsListProps {
  runs: (Run & { match?: Match })[];
  isOwnProfile: boolean;
  onTogglePublic: (runId: string, isPublic: boolean) => void;
  onBeatRun: (run: Run) => void;
}

export function RunsList({ runs, isOwnProfile, onTogglePublic, onBeatRun }: RunsListProps) {
  if (runs.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        No runs yet. Start playing to see your history!
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {runs.map((run) => (
        <div key={run.id} className="game-card">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                {run.is_public ? (
                  <Globe className="w-4 h-4 text-primary shrink-0" />
                ) : (
                  <Lock className="w-4 h-4 text-muted-foreground shrink-0" />
                )}
                <span className="font-semibold truncate">
                  {run.match?.start_title} → {run.match?.target_title}
                </span>
              </div>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span className={`mode-badge ${run.match?.mode === 'time_attack' ? 'time-attack' : 'under-clicks'}`}>
                  {run.match && MODE_LABELS[run.match.mode]}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatTime(run.duration_ms || 0)}
                </span>
                <span className="flex items-center gap-1">
                  <MousePointer2 className="w-3 h-3" />
                  {run.clicks} clicks
                </span>
                {!run.completed && (
                  <span className="text-destructive">DNF</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isOwnProfile && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Public</span>
                  <Switch
                    checked={run.is_public}
                    onCheckedChange={(checked) => onTogglePublic(run.id, checked)}
                  />
                </div>
              )}
              {run.is_public && run.completed && (
                <Button variant="outline" size="sm" onClick={() => onBeatRun(run)} className="gap-1">
                  <Swords className="w-3 h-3" />
                  Beat
                </Button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}